import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Loader2, AlertCircle, Search, Plus } from 'lucide-react';
import axiosInstance from '../api/axios';

/**
 * Single-pick dropdown of org documents (GET /api/reports catalog).
 * Used on the flowchart node edit form to attach a catalog document to the node.
 *
 *   excludeIds  string[]                          — already-attached document UUIDs (hidden)
 *   onSelect    (doc: { id, name, docType, systems }) => void
 */
function DocumentCatalogSelect({ excludeIds, onSelect }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const excluded = useMemo(
    () => new Set((Array.isArray(excludeIds) ? excludeIds : []).filter(Boolean)),
    [excludeIds]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const PAGE_SIZE = 100;
        const all = [];
        let page = 1;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const res = await axiosInstance.get('/api/reports', {
            params: { page, page_size: PAGE_SIZE },
          });
          if (cancelled) return;
          const batch = Array.isArray(res.data?.reports) ? res.data.reports : [];
          all.push(...batch);
          const totalPages = Number(res.data?.pagination?.total_pages) || 1;
          if (page >= totalPages || batch.length === 0) break;
          page += 1;
        }
        if (!cancelled) setDocuments(all);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.error || 'Failed to load documents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const available = useMemo(
    () => documents.filter((d) => d?.id && !excluded.has(d.id)),
    [documents, excluded]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter((d) => {
      const name = (d.name || '').toLowerCase();
      const code = (d.reportId || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [available, query]);

  const handlePick = (row) => {
    if (!row?.id || typeof onSelect !== 'function') return;
    const isReport = String(row.docType || '').toLowerCase() === 'report';
    onSelect({
      id: row.id,
      name: row.name || '',
      docType: isReport ? 'report' : null,
      systems: Array.isArray(row.systems)
        ? row.systems
          .map((s) => ({
            id: typeof s?.id === 'string' ? s.id.trim() : '',
            name: typeof s?.name === 'string' ? s.name.trim() : '',
          }))
          .filter((s) => s.id && s.name)
        : [],
    });
    setQuery('');
    setOpen(false);
  };

  const goToCreate = () => {
    setOpen(false);
    navigate('/sox-reports/new', {
      state: {
        fromSystem: {
          returnPath: `${location.pathname}${location.search || ''}`,
          prefilledName: query.trim() || '',
        },
      },
    });
  };

  const exactMatch =
    query.trim().length > 0 &&
    documents.some((d) => (d.name || '').toLowerCase() === query.trim().toLowerCase());
  const showAddNewHint = query.trim().length > 0 && !exactMatch;

  return (
    <div ref={rootRef} className="relative mb-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between min-h-[42px] px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className="text-sm text-gray-400">Select document…</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search documents…"
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="max-h-52 overflow-y-auto">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading documents…
              </div>
            ) : loadError ? (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {loadError}
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-400">
                {available.length === 0 ? 'No more documents to attach' : 'No matching documents'}
              </p>
            ) : (
              filtered.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => handlePick(d)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2"
                >
                  <span className="truncate text-gray-800">{d.name || 'Untitled'}</span>
                  {d.reportId ? (
                    <span className="shrink-0 font-mono text-xs text-gray-400">{d.reportId}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>

          <div className="border-t border-gray-100 p-1 bg-gray-50">
            <button
              type="button"
              onClick={goToCreate}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded text-blue-700 hover:bg-blue-50"
            >
              <Plus className="h-4 w-4" />
              {showAddNewHint ? (
                <>Add document <strong>“{query.trim()}”</strong></>
              ) : (
                'Add document'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentCatalogSelect;
